/**
 * MyPricing — customer-facing read-only pricing matrix.
 *
 * Sachin spec 2026-02-17: parents/athletes get a "Current Pricing" button
 * that shows the rates so they can see WHY the 2:1 cancellation
 * conversion exists (non-member = ~2x member rate).
 *
 * Reads /api/me/pricing. No edit controls — pure read-only.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Info, Tag, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function CategoryBlock({ category }) {
    if (!category.skus.length) return null;
    return (
        <section
            data-testid={`pricing-cat-${category.key}`}
            className="border border-zinc-800 bg-zinc-950/60 p-4 mb-4"
        >
            <h2 className="text-[11px] tracking-[0.28em] uppercase text-zinc-300 font-display font-bold mb-3 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-brand-red" />
                {category.label}
            </h2>
            <div className="divide-y divide-zinc-900">
                {category.skus.map((s) => (
                    <div
                        key={s.sku_id}
                        data-testid={`pricing-sku-${s.sku_id}`}
                        className="flex items-baseline justify-between gap-3 py-2.5"
                    >
                        <div className="min-w-0">
                            <div className="text-sm text-white font-medium truncate">{s.display_name}</div>
                            {s.description && (
                                <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{s.description}</div>
                            )}
                        </div>
                        <div className="text-right whitespace-nowrap">
                            <div className="font-mono text-base text-white font-semibold">
                                {fmtMoney(s.totals?.total_cents ?? s.price_cents)}
                            </div>
                            <div className="text-[10px] text-zinc-500">
                                {fmtMoney(s.price_cents)} + {fmtMoney(s.totals?.tax_cents || 0)} tax
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

export default function MyPricing() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    // Sachin 2026-03-05: "The only way to see prices should be when I
    // send manually." A lead landing on this URL directly must see a
    // friendly blocked screen, not the price table.
    const [gated, setGated] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`${API}/me/pricing`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch((e) => {
                const detail = e?.response?.data?.detail;
                // Backend returns {visible:false, reason:"lead_send_manual"}
                // wrapped in a 403 for lead accounts. Detect and render the
                // gated view instead of a raw error.
                if (e?.response?.status === 403 && detail && typeof detail === "object" && detail.visible === false) {
                    setGated(true);
                } else {
                    setErr(typeof detail === "string" ? detail : "Couldn't load pricing");
                }
            })
            .finally(() => setLoading(false));
    }, []);

    if (gated) {
        return (
            <div data-testid="my-pricing-gated" className="max-w-md mx-auto px-6 py-16 text-center">
                <button
                    type="button"
                    data-testid="my-pricing-back"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white mb-10"
                >
                    <ArrowLeft className="w-3 h-3" />
                    Back
                </button>
                <div className="inline-flex items-center justify-center w-12 h-12 border border-zinc-800 bg-zinc-950 mb-5">
                    <Tag className="w-5 h-5 text-brand-red" />
                </div>
                <h1 className="font-display text-2xl tracking-wider text-white mb-3">
                    Prices are handed off in person.
                </h1>
                <p className="text-sm text-zinc-400 leading-relaxed">
                    We don&apos;t self-serve pricing — your coach will walk you through the options and send them over directly after your assessment.
                </p>
                <p className="text-xs text-zinc-500 mt-6">
                    Already had your assessment? Check your email for the recap link, or reply to any note from us and we&apos;ll resend it.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="my-pricing-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
            <button
                type="button"
                data-testid="my-pricing-back"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white mb-4"
            >
                <ArrowLeft className="w-3 h-3" />
                Back
            </button>
            <h1 className="font-display text-3xl tracking-wider text-white">Current Pricing</h1>
            <p className="text-[12px] text-zinc-400 mt-1 leading-snug">
                Live rates pulled from the gym. Includes tax. Members pay roughly half what non-members pay — that&apos;s why the 2:1 conversion applies if you cancel.
            </p>

            {data?.policy?.cancellation_explanation && (
                <div
                    data-testid="my-pricing-cancellation-policy"
                    className="mt-4 border border-amber-700/60 bg-amber-900/15 px-3 py-2.5 text-[12px] text-amber-100 leading-snug flex items-start gap-2"
                >
                    <Info className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                    <div>
                        <span className="font-semibold text-amber-200">Cancellation policy ({data.policy.cancellation_conversion_ratio}):</span>{" "}
                        {data.policy.cancellation_explanation}
                    </div>
                </div>
            )}

            <div className="mt-5">
                {loading ? (
                    <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : err ? (
                    <div className="text-sm text-red-300" data-testid="my-pricing-error">{err}</div>
                ) : !data ? null : (
                    <>
                        {data.categories.map((cat) => (
                            <CategoryBlock key={cat.key} category={cat} />
                        ))}
                        <p className="text-[10px] text-zinc-600 text-center mt-2 tracking-wide">
                            Pricing subject to change. Questions? Message your coach.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
